import './style.css';

import { applyTranslations, getLocale, initI18n, onLocaleChange, setLocale } from '@/i18n';
import type { Locale, TranslationKey } from '@/i18n';
import { getSupabase, isSupabaseConfigured } from '@/net/supabase';
import { CityRenderer } from '@/render/cityRenderer';
import { createGrid, INITIAL_GRID_SIZE } from '@/render/isoEngine';
import { createGridHud } from '@/ui/gridHud';
import { createGame } from '@/game';
import { installIconSprite } from '@/ui/icons';
import { applyTokens } from '@/ui/tokens';
import { SoundEngine } from '@/audio/sound';

/**
 * Moteur audio du jeu. Échantillons Kenney sous CC0 (voir CREDITS.md).
 *
 * **Rien n'est chargé avant le premier geste** : instancier un `Howl` crée
 * l'`AudioContext`, que le navigateur bloque tant que l'utilisateur n'a pas interagi —
 * la console se remplissait d'avertissements « AudioContext was not allowed to start »
 * et Howler réessayait en boucle. Les échantillons (81 Ko d'ogg) se déclarent au
 * premier `pointerdown`, en même temps que le déblocage : chargés bien avant le
 * premier son à jouer.
 */
export const soundEngine = new SoundEngine();

// Les tokens de design sont posés avant tout rendu : les feuilles de style ne
// contiennent que des références, jamais de valeurs (D§2).
applyTokens();

/**
 * Point d'entrée de Villopia.
 *
 * Phase 0 / étape 3 : grille isométrique extensible et caméra navigable.
 * L'économie, la boutique et le placement arrivent en Phase 1.
 */

/** Une extension de terrain ajoute une bande sur les deux axes (§6). */
const GRID_STEP = 2;

function requireElement(selector: string): HTMLElement {
  const element = document.querySelector<HTMLElement>(selector);
  if (element === null) {
    throw new Error(`Élément introuvable dans le document : ${selector}`);
  }
  return element;
}

function isLocaleValue(value: string | undefined): value is Locale {
  return value === 'fr' || value === 'en';
}

function setupLocaleSwitchers(): void {
  const buttons = document.querySelectorAll<HTMLButtonElement>('[data-locale]');

  const syncPressedState = (locale: Locale): void => {
    for (const button of buttons) {
      button.setAttribute('aria-pressed', String(button.dataset.locale === locale));
    }
  };

  for (const button of buttons) {
    button.addEventListener('click', () => {
      const target = button.dataset.locale;
      if (isLocaleValue(target)) {
        setLocale(target);
      }
    });
  }

  onLocaleChange(syncPressedState);
  syncPressedState(getLocale());
}

// Typé `TranslationKey` et non `string` : l'indirection ne doit pas rouvrir la brèche
// que `applyTranslations` ouvre déjà en castant les attributs HTML.
function setBootMessage(statusKey: TranslationKey, hintKey?: TranslationKey): void {
  const status = requireElement('#boot-status');
  const hint = requireElement('#boot-hint');

  status.dataset.i18n = statusKey;
  if (hintKey === undefined) {
    delete hint.dataset.i18n;
    hint.textContent = '';
  } else {
    hint.dataset.i18n = hintKey;
  }
  applyTranslations(requireElement('#boot'));
}

function hideBootScreen(): void {
  const boot = requireElement('#boot');
  boot.classList.add('boot--hidden');
  // On retire l'écran du flux une fois la transition finie, pour qu'il ne capte plus
  // aucun geste au-dessus du canvas.
  globalThis.setTimeout(() => {
    boot.hidden = true;
  }, 400);
}

function showErrorState(error: unknown): void {
  console.error('[villopia] démarrage impossible', error);
  requireElement('#boot').classList.add('boot--error');
  setBootMessage('boot.error', 'boot.errorHint');
}

/**
 * Hôte canonique du jeu. Tout le reste y est renvoyé — sauf les prévisualisations.
 *
 * ── Pourquoi une redirection, et pas seulement un joli domaine ────────────────
 *
 * `villopia.app` et `villopia.pages.dev` sont **deux origines** pour le navigateur, donc
 * deux `localStorage`, donc **deux villes séparées**. Un joueur qui atterrit une fois sur
 * l'une et une fois sur l'autre a littéralement deux parties, et croit en avoir perdu une.
 * C'est la même racine que le lien magique qui renvoyait sur la mauvaise origine (corrigé
 * côté Supabase) : une identité de joueur ne survit pas à un changement d'hôte.
 *
 * Cloudflare ne permet pas de supprimer l'URL `*.pages.dev` d'un projet Pages — c'est son
 * adresse canonique par construction — et aucune règle de redirection du tableau de bord
 * ne peut l'atteindre, la zone `pages.dev` n'appartenant pas au compte. La redirection se
 * fait donc ici, au démarrage, avant tout téléchargement d'asset.
 *
 * **L'égalité est STRICTE.** Un `endsWith('.pages.dev')` emporterait les déploiements de
 * prévisualisation (`<hash>.villopia.pages.dev`), donc la possibilité de tester une branche
 * avant de la mettre en ligne. Seul l'hôte de production est renvoyé.
 *
 * Chemin, requête et fragment sont conservés : un lien `/ville/:slug` déjà partagé doit
 * continuer d'ouvrir la bonne ville. `replace` et non `assign`, pour que le bouton retour
 * ne fasse pas d'aller-retour entre les deux domaines.
 */
const LEGACY_HOST = 'villopia.pages.dev';
const CANONICAL_ORIGIN = 'https://villopia.app';

function redirectToCanonicalHost(): boolean {
  if (location.hostname !== LEGACY_HOST) return false;
  location.replace(`${CANONICAL_ORIGIN}${location.pathname}${location.search}${location.hash}`);
  return true;
}

async function bootstrap(): Promise<void> {
  // Avant TOUT le reste : inutile de charger l'atlas et d'ouvrir une session sur une
  // origine qu'on quitte dans la milliseconde.
  if (redirectToCanonicalHost()) return;

  // E§3 : les outils de mise au point existent, mais derrière `?debug`. En production,
  // ni compteur de fps, ni panneau de grille, ni boutons de redimensionnement.
  const debug = new URLSearchParams(location.search).has('debug');
  if (debug) {
    document.body.classList.add('is-debug');
    requireElement('#grid-hud').hidden = false;
  }

  initI18n();
  applyTranslations();

  /*
    Retour de lien magique : l'URL porte un `code` à échanger contre une session. Le
    client Supabase est volontairement paresseux (§1) ; dans ce cas précis on le crée
    tout de suite, sinon l'échange attendrait la première synchro et l'écran Compte
    mentirait pendant quelques secondes.
  */
  if (isSupabaseConfigured() && new URLSearchParams(location.search).has('code')) {
    getSupabase();
  }
  // Sans cela, chaque `<use href="#vi-…">` du jeu rend du vide : les icônes des cartes
  // de boutique et des boutons disparaissent en silence.
  installIconSprite();
  setupLocaleSwitchers();
  setBootMessage('boot.loadingAssets');

  const canvas = requireElement('#game-canvas');
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new TypeError('#game-canvas n’est pas un élément <canvas>.');
  }

  const renderer = new CityRenderer({
    canvas,
    // La taille de rendu vient du conteneur, jamais du canvas : PixiJS lui impose un style
    // inline, et le mesurer l'empêcherait de grandir avec la fenêtre.
    container: requireElement('#app'),
    grid: createGrid(INITIAL_GRID_SIZE, INITIAL_GRID_SIZE),
  });

  await renderer.init();

  const game = createGame({
    renderer,
    canvas,
    sound: soundEngine,
    resourcesHost: requireElement('#hud-host'),
    shopHost: requireElement('#shop-host'),
  });
  game.start();

  const hud = createGridHud({
    onExpand: () => {
      renderer.setGridSize(GRID_STEP, GRID_STEP);
    },
    onShrink: () => {
      renderer.setGridSize(-GRID_STEP, -GRID_STEP);
    },
    onRecenter: () => {
      renderer.recenter();
    },
  });

  /*
    Le HUD de mise au point ne tourne QUE sous `?debug` (audit § U11).

    Cette boucle rafraîchissait un panneau masqué en production, soixante fois par
    seconde, pour toujours : `getStatus()` interroge le ticker, compte les enfants du
    calque et lit la caméra, et le résultat n'était affiché à personne. Ce n'était pas
    gratuit — c'est une frame de travail par frame de rendu, sur des téléphones dont le
    §8 fait la cible.

    Et le rythme passe de 60 à 4 fois par seconde même en debug : un compteur de fps qui
    change soixante fois par seconde est illisible, et une mesure qu'on ne peut pas lire
    ne sert à rien.
  */
  if (debug) {
    let timer = 0;
    const refresh = (): void => {
      hud.update(renderer.getStatus());
      timer = window.setTimeout(refresh, 250);
    };
    refresh();
    window.addEventListener('pagehide', () => {
      globalThis.clearTimeout(timer);
    });
  }

  hideBootScreen();
}

/**
 * Débloque l'audio au tout premier geste réel du joueur.
 *
 * Les navigateurs refusent de démarrer un contexte audio sans interaction, et un
 * contexte créé trop tôt reste suspendu **sans le dire** : tous les sons partiraient
 * dans le vide. On attend donc un `pointerdown`, une seule fois.
 */
function armAudioUnlock(): void {
  const unlock = (): void => {
    // L'ordre compte : les `Howl` se créent PENDANT le geste, donc le contexte audio
    // naît autorisé — aucun avertissement, aucun réessai en boucle de Howler.
    soundEngine.loadDefaultSamples();
    soundEngine.unlock();
    document.removeEventListener('pointerdown', unlock);
    document.removeEventListener('keydown', unlock);
  };
  document.addEventListener('pointerdown', unlock, { once: false });
  document.addEventListener('keydown', unlock, { once: false });
}

armAudioUnlock();

bootstrap().catch((error: unknown) => {
  showErrorState(error);
});
